import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product, ProductVariant, Region } from '../types';
import { Badge, ImageWithLoader } from './ui';
import { formatPrice, formatDiscount, getEditionName, accountRate, formatPlatform } from '../utils/helpers';
import { useSettingsStore, useUserStore } from '../store';
import { optimizeImage } from '../utils/image';
import { rememberScrollAnchor } from '../hooks/useScrollRestoration';

interface ProductCardProps {
  product: Product;
  orientation?: 'horizontal' | 'vertical';
  /** Плитка одной версии игры («для PS4» / «для PS4/PS5») в поиске: цена и подпись
   *  по этому предложению, карточка открывается сразу с ним (?platform=). */
  variant?: ProductVariant;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  orientation = 'vertical',
  variant,
}) => {
  const navigate = useNavigate();
  const { settings } = useSettingsStore();
  const userRegion = useUserStore((s) => s.lastSelectedRegion);

  const { displayPrice, displayDiscount } = useMemo(() => {
    if (!product.variants || product.variants.length === 0) {
      return { displayPrice: product.minPrice, displayDiscount: null as number | null };
    }
    const computePrice = (v: typeof product.variants[number]) => {
      const rate = accountRate(v.region, v.regionalPrice || 0, product.rateOverrides, settings);
      return (rate && v.regionalPrice)
        ? Math.ceil((v.regionalPrice * rate) / 5) * 5
        : v.price;
    };
    if (variant) {
      return { displayPrice: computePrice(variant), displayDiscount: variant.discount ?? null };
    }
    const pickCheapest = (vs: typeof product.variants) => {
      const withPrice = vs
        .map(v => ({ v, p: computePrice(v) }))
        .filter(x => x.p > 0);
      if (!withPrice.length) return null;
      return withPrice.reduce((min, cur) => cur.p < min.p ? cur : min);
    };
    if (userRegion) {
      // Регион пользователя показываем ВСЕГДА (как на странице товара), не завязываясь на
      // флаг availableRegions — иначе цена в карточке и на странице товара расходятся.
      const regional = product.variants
        .filter(v => v.available && v.region === userRegion);
      const best = pickCheapest(regional);
      if (best) return { displayPrice: best.p, displayDiscount: best.v.discount ?? null };
    }
    const all = product.variants
      .filter(v => v.available && settings.availableRegions[v.region as Region]);
    const best = pickCheapest(all);
    if (best) return { displayPrice: best.p, displayDiscount: best.v.discount ?? null };
    return { displayPrice: product.minPrice, displayDiscount: null };
  }, [product, variant, settings.exchangeRates, settings.rangePricing, settings.availableRegions, userRegion]);

  const cardSubtitle = useMemo(() => {
    if (product.category === 'donat') return 'Дополнение';
    if (product.category === 'game') {
      const edition = product.variants?.find(v => v.edition)?.edition;
      const label = edition ? getEditionName(edition) : 'Standard Edition';
      return variant?.platform ? `${label} · ${formatPlatform(variant.platform)}` : label;
    }
    return product.subtitle || null;
  }, [product, variant]);

  const handleClick = () => {
    // Запоминаем, с какой карточки ушли: при возврате список поставит ее на то же
    // место экрана, даже если отрисовался другой длины.
    rememberScrollAnchor(product.id);
    const platformQuery = variant?.platform ? `?platform=${encodeURIComponent(variant.platform)}` : '';
    navigate(`/product/${product.id}${platformQuery}`);
  };

  if (orientation === 'horizontal') {
    return (
      <div
        data-restore-id={product.id}
        className="flex-shrink-0 w-[240px] bg-white rounded-card border border-gray-200/80 overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
        onClick={handleClick}
      >
        <div className="relative">
          <ImageWithLoader
            src={optimizeImage(product.coverImage, 400)}
            alt={product.name}
            wrapperClassName="w-full h-[130px]"
            className="object-cover"
            spinnerSize={20}
            fallbackSrc="https://via.placeholder.com/280x140/f3f4f6/9ca3af?text=PS"
          />
          {displayDiscount ? (
            <div className="absolute top-2 left-2 z-20">
              <Badge variant="discount" size="sm">
                {formatDiscount(displayDiscount)}
              </Badge>
            </div>
          ) : null}
        </div>

        <div className="p-3">
          <h3 className="line-clamp-1 mb-0.5" style={{ fontFamily: 'Manrope, sans-serif', fontSize: '17px', fontWeight: 700, lineHeight: '22px', letterSpacing: '-0.2px', color: '#1A1D1A' }}>
            {product.name}
          </h3>
          {cardSubtitle && (
            <p className="line-clamp-1 mb-1" style={{ fontSize: '13px', fontWeight: 400, lineHeight: '16px', letterSpacing: '-0.08px', color: '#818384' }}>{cardSubtitle}</p>
          )}
          <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '17px', fontWeight: 700, lineHeight: '22px', color: '#1A1D1A' }}>
            {formatPrice(displayPrice)}
          </p>
        </div>
      </div>
    );
  }

  // Vertical card for catalog grid (matches HomePage style)
  return (
    <div
      data-restore-id={product.id}
      className="cursor-pointer active:scale-[0.97] transition-transform"
      onClick={handleClick}
    >
      <div className="relative mb-2.5">
        <ImageWithLoader
          src={optimizeImage(product.coverImage, 400)}
          alt={product.name}
          wrapperClassName="w-full aspect-square rounded-[14px]"
          className="object-cover"
          spinnerSize={24}
        />
      </div>
      <p className="truncate mb-0.5" style={{ fontFamily: 'Manrope, sans-serif', fontSize: '17px', fontWeight: 700, lineHeight: '22px', letterSpacing: '-0.2px', color: '#1A1D1A' }}>
        {product.name}
      </p>
      {cardSubtitle && (
        <p className="truncate" style={{ fontSize: '13px', fontWeight: 400, lineHeight: '16px', letterSpacing: '-0.08px', color: '#818384' }}>{cardSubtitle}</p>
      )}
      <div className="flex items-center gap-1.5 mt-0.5">
        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '17px', fontWeight: 700, lineHeight: '22px', color: '#1A1D1A' }}>
          {formatPrice(displayPrice)}
        </p>
        {displayDiscount ? (
          <span className="bg-brand-gradient text-white text-[13px] font-bold px-1.5 py-0.5 rounded-md">
            {formatDiscount(displayDiscount)}
          </span>
        ) : null}
      </div>
    </div>
  );
};
